import { ArrowDownRight, ArrowUpRight, type LucideIcon, Minus } from 'lucide-react';
import { AnimatedNumber } from './AnimatedNumber';
import { SectionCard } from './ui/SectionCard';
import { cn } from '@/lib/cn';

type Props = {
  label: string;
  value: number;
  format: (v: number) => string;
  delta?: number;
  deltaFormat?: (v: number) => string;
  hint?: string;
  icon?: LucideIcon;
  accent?: 'default' | 'emerald' | 'rose' | 'amber' | 'indigo';
  highlight?: boolean;
};

export function KpiCard({
  label,
  value,
  format,
  delta,
  deltaFormat,
  hint,
  icon: Icon,
  accent = 'default',
  highlight = false,
}: Props) {
  const accentColor =
    accent === 'emerald'
      ? 'text-emerald-400'
      : accent === 'rose'
        ? 'text-rose-400'
        : accent === 'amber'
          ? 'text-amber-400'
          : accent === 'indigo'
            ? 'text-indigo-400'
            : 'text-zinc-400';

  return (
    <SectionCard className={cn('relative overflow-hidden', highlight && 'grad-border')}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider text-zinc-400">{label}</span>
        {Icon && <Icon className={cn('size-4', accentColor)} />}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-zinc-50 num">
          <AnimatedNumber value={value} format={format} />
        </span>
        {typeof delta === 'number' && (
          <span
            className={cn(
              'inline-flex items-center text-xs num',
              delta > 0
                ? 'text-emerald-400'
                : delta < 0
                  ? 'text-rose-400'
                  : 'text-zinc-400',
            )}
          >
            {delta > 0 ? (
              <ArrowUpRight className="size-3" />
            ) : delta < 0 ? (
              <ArrowDownRight className="size-3" />
            ) : (
              <Minus className="size-3" />
            )}
            {deltaFormat ? deltaFormat(delta) : `${delta.toFixed(1)}%`}
          </span>
        )}
      </div>
      {hint && <div className="mt-1 text-[11px] text-zinc-400">{hint}</div>}
    </SectionCard>
  );
}
